import { useState } from 'react';
import { problemDetail } from '@/shared/api/problem';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetApiV1MissionsCommitteeQueueQueryKey,
  useGetApiV1MissionsCommitteeQueue,
  usePostApiV1MissionsMissionIdVotes,
} from '@/shared/api/generated/missions/missions';
import type { CommitteeQueueItemDto } from '@/shared/api/generated/model';
import { VoteDecision } from '@/shared/api/generated/model';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { formatDateTime, formatElapsed } from '@/shared/lib/format/dates';
import { Button } from '@/shared/ui/Button';
import { QueryError } from '@/shared/ui/QueryError';
import { Chip } from '@/shared/ui/Chip';
import { LiveEvery, liveEvery } from '@/shared/lib/live';

/**
 * The committee's voting queue (§23.2).
 *
 * The backend has supported voting since the missing-person slice; until now there was no way to cast
 * one — which made the whole committee gate unreachable outside a test.
 */
export function CommitteeQueue() {
  const { t, locale } = useLocale();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const params = {};
  const queue = useGetApiV1MissionsCommitteeQueue(params, liveEvery(LiveEvery.queue));

  const vote = usePostApiV1MissionsMissionIdVotes({
    mutation: {
      onSuccess: async () => {
        setError(null);
        // The row leaves the queue once quorum is reached, and the server decides that — so the list is
        // refetched rather than patched.
        await queryClient.invalidateQueries({
          queryKey: getGetApiV1MissionsCommitteeQueueQueryKey(params),
        });
      },
      onError: (cause: unknown) => {
        setError(problemDetail(cause) ?? t.committee.voteFailed);
      },
    },
  });

  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-col gap-1.5">
        <h1 className="text-2xl font-bold tracking-tight">{t.committee.heading}</h1>
        <p className="text-sm text-muted-foreground">{t.committee.lede}</p>
      </header>

      {error ? (
        <p role="alert" className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
          {error}
        </p>
      ) : null}

      {queue.isPending ? (
        <p className="text-sm text-muted-foreground">{t.app.loading}</p>
      ) : queue.isError ? (
        <QueryError error={queue.error} onRetry={() => void queue.refetch()} />
      ) : queue.data.items.length === 0 ? (
        <p className="rounded-md border border-border bg-surface px-4 py-6 text-center text-sm text-muted-foreground">
          {t.committee.empty}
        </p>
      ) : (
        <ul className="flex flex-col gap-4">
          {queue.data.items.map((item) => (
            <Card
              key={item.missionId}
              item={item}
              locale={locale}
              busy={vote.isPending}
              onVote={(decision) => vote.mutate({ missionId: item.missionId, data: { decision, comment: null } })}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * ⚠️ A card, not a table row.
 *
 * A vote is a decision about one person, and a table invites scanning down a column and clicking. The
 * card forces the reader past the name, the age, the time missing and the last sighting before the
 * buttons — which is the information §23.2 expects the decision to be made on.
 */
function Card({
  item,
  locale,
  busy,
  onVote,
}: {
  item: CommitteeQueueItemDto;
  locale: 'ar' | 'en';
  busy: boolean;
  onVote: (decision: VoteDecision) => void;
}) {
  const { t } = useLocale();

  return (
    <li className="flex flex-col gap-4 rounded-lg border border-border bg-surface p-4">
      <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
        <div className="flex flex-col">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">
            {t.committee.person}
          </span>
          <span className="text-lg font-bold">{item.missingFullName}</span>
        </div>

        {item.age !== null && item.age !== undefined ? (
          <Field label={t.committee.age} value={`${item.age} ${t.committee.years}`} />
        ) : null}

        <Field
          label={t.committee.missedAt}
          value={`${formatDateTime(item.missedAt, locale)} · ${formatElapsed(item.missedAt, locale)}`}
        />

        {/* ⚠️ Only that restricted notes EXIST (§12.3). Their content is behind its own endpoint and its
            own policy — a committee member is not necessarily allowed to read them, and putting them in
            this list would hand them to everyone who can vote. */}
        {item.hasRestrictedNotes ? (
          <Chip tone="pending">{t.committee.restrictedNotes}</Chip>
        ) : null}
      </div>

      {item.lastSeenDescription ? (
        <Field label={t.committee.lastSeen} value={item.lastSeenDescription} />
      ) : null}

      {item.clothingDescription ? (
        <Field label={t.committee.clothing} value={item.clothingDescription} />
      ) : null}

      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <Field
          label={t.committee.votes}
          value={`${item.approvals} ${t.committee.ofRequired} ${item.requiredApprovals}`}
        />

        {item.deadlineAt ? (
          <Field
            label={t.committee.deadline}
            value={
              item.deadlinePassed
                ? t.committee.deadlinePassed
                : formatDateTime(item.deadlineAt, locale)
            }
          />
        ) : null}

        {/* ms-auto so the actions sit at the inline end in both writing directions. */}
        <div className="ms-auto flex items-center gap-2">
          {item.iAlreadyVoted ? (
            <Chip tone="approved">{t.committee.voted}</Chip>
          ) : (
            <>
              <Button disabled={busy} onClick={() => onVote(VoteDecision.Approve)}>
                {t.committee.approve}
              </Button>
              <Button variant="danger" disabled={busy} onClick={() => onVote(VoteDecision.Reject)}>
                {t.committee.reject}
              </Button>
            </>
          )}
        </div>
      </div>
    </li>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
